import React, { useState } from "react";
import { isEqual } from "lodash";
import {
  Box,
  Collapse,
  CSSObject,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconCornerDownRight,
} from "@tabler/icons-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import RoutesString from "pages/routesString";

interface LinkItem {
  id: string;
  label: string;
  link: string;
  linkDetail?: string;
}

interface LinksGroupProps {
  id: string;
  icon: React.FC<any>;
  iconActive?: React.FC<any>;
  label: string;
  initiallyOpened?: boolean; // kept for MenuConfig compatibility
  root?: string;
  links?: LinkItem[];
  link?: string;
  openedMenu: any;
  setOpenedMenu: any;
}

interface LinkLiteProps {
  icon: React.FC<any>;
  iconActive?: React.FC<any>;
  label: string;
  root?: string;
  toggleSideBar(collapsed: boolean): void;
}

const activeLink: CSSObject = {
  backgroundColor: "#E5F4FE",
  color: "#1368BC",
  borderRadius: "12px",
};

const compactButton: CSSObject = {
  width: "50px",
  height: "50px",
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#2E3337",
  transition: "all 150ms ease",
};

/**
 * Normal link component for expanded sidebar
 */
function LinksNormal({
  icon: Icon,
  iconActive: ActiveIcon,
  label,
  id,
  links,
  root,
  link: linkUrl,
  openedMenu,
  setOpenedMenu,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  initiallyOpened: _initiallyOpened,
}: LinksGroupProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const hasLinks = Array.isArray(links) && links.length > 0;
  const isOpened = openedMenu === label;
  const ChevronIcon = isOpened ? IconChevronUp : IconChevronDown;

  const isActive = () => {
    if (location.pathname === RoutesString.Dashboard) return !root;
    return root && location.pathname.includes(`/${root}`);
  };

  const active = isActive();
  const IconToUse = active && ActiveIcon ? ActiveIcon : Icon;

  const getIconColor = () => {
    if (active || isOpened || isHovered) return "#1368BC";
    return "#2E3337";
  };

  const handleMenuToggle = () => {
    if (hasLinks) {
      setOpenedMenu(isOpened ? null : label);
      if (linkUrl) navigate(linkUrl);
    } else if (linkUrl) {
      navigate(linkUrl);
    }
  };

  const submenuItems = hasLinks
    ? links.map((link) => {
        const isLinkActive =
          link.link === location.pathname ||
          !!(link.linkDetail && location.pathname.includes(link.linkDetail));
        return (
          <UnstyledButton
            key={link.id}
            component={Link}
            to={link.link}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              textDecoration: "none",
              padding: "8px 12px",
              fontWeight: 400,
              fontSize: "14px",
              color: isLinkActive ? "#1368BC" : "#2E3337",
              borderRadius: "8px",
              transition: "all 150ms ease",
              backgroundColor: isLinkActive ? "#F8F9FB" : "transparent",
              "&:hover": { backgroundColor: "#F8F9FB" },
            }}
            fz={14}
          >
            <IconCornerDownRight
              size="1rem"
              style={{
                marginRight: "17px",
                marginTop: "1px",
                color: isLinkActive ? "#1368BC" : "inherit",
                flexShrink: 0,
              }}
            />
            <Box sx={{ flex: 1, wordBreak: "break-word", whiteSpace: "normal" }}>
              {link.label}
            </Box>
          </UnstyledButton>
        );
      })
    : null;

  // Derive header background
  const headerBg = () => {
    if (active && !hasLinks) return "#E5F4FE";
    if (isOpened) return "#E5F4FE";
    if (isHovered && !isOpened) return "#E5F4FE";
    return "transparent";
  };

  return (
    // Outer UnstyledButton: full width, block so hover covers the whole row
    <UnstyledButton
      key={id}
      display="block"
      w="100%"
      mb={8}
      onClick={handleMenuToggle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Column container — replaces Group so nothing sits beside the header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        {/* Header row: icon + label + optional chevron */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            padding: "10px 12px",
            borderRadius: isOpened ? "12px 12px 0 0" : "12px",
            backgroundColor: headerBg(),
            color: active || isOpened || isHovered ? "#1368BC" : "#2E3337",
            fontWeight: 600,
            fontSize: "14px",
            transition: "all 150ms ease",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <IconToUse size="1.1rem" color={getIconColor()} />
            <Box sx={{ wordBreak: "break-word", whiteSpace: "normal" }}>
              {label}
            </Box>
          </Box>
          {hasLinks && (
            <ChevronIcon
              size="1rem"
              stroke={1.5}
              style={{ color: getIconColor(), flexShrink: 0 }}
            />
          )}
        </Box>

        {/* Submenu — stacks BELOW the header */}
        {hasLinks && (
          <Collapse in={!!(isOpened || active)} w="100%">
            <Box
              sx={{
                width: "100%",
                backgroundColor: "#F0F2F7",
                padding: "4px",
                borderRadius: "0 0 12px 12px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {submenuItems}
            </Box>
          </Collapse>
        )}
      </Box>
    </UnstyledButton>
  );
}

/**
 * Compact link component for collapsed sidebar
 */
function LinkLite({
  icon: Icon,
  iconActive: ActiveIcon,
  label,
  root,
  toggleSideBar,
}: LinkLiteProps) {
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);

  const isActive = () => {
    if (location.pathname === RoutesString.Dashboard) return isEqual(root, "");
    return root && location.pathname.slice(1)?.includes(root);
  };

  const active = isActive();
  const IconToUse = active && ActiveIcon ? ActiveIcon : Icon;

  const getIconColor = () => {
    if (active || isHovered) return "#1368BC";
    return "#2E3337";
  };

  return (
    <Tooltip label={label} position="right" transitionProps={{ duration: 0 }}>
      <UnstyledButton
        onClick={() => toggleSideBar(true)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          ...compactButton,
          "&:hover": { backgroundColor: "#E5F4FE" },
          ...(active && { "&, &:hover": activeLink }),
        }}
      >
        <IconToUse size="1.2rem" stroke={1.5} style={{ color: getIconColor() }} />
      </UnstyledButton>
    </Tooltip>
  );
}

export { LinksNormal, LinkLite };
